import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown, Search, Check, Tag } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  buildCategoryTree,
  filterCategoriesByQuery,
  normalizeCategoriesResponse,
  orderRecentFirst,
  readRecentIds,
  recordRecentId,
  type Category,
  type CategoryKind,
} from "./categoryHelpers";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";

export interface CategoryPickerProps {
  kind: CategoryKind;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Record + surface recent-first selection. Default true. */
  recentFirst?: boolean;
  className?: string;
  disabled?: boolean;
}

async function fetchCategories(kind: CategoryKind): Promise<Category[]> {
  // includeArchived=true so an already-selected archived category (the
  // keep-selected-archived logic below) can still be found and rendered —
  // the `selectable` filter still excludes archived categories from the
  // rest of the picker.
  const res = await apiFetch(apiUrl("categories", "", { kind, includeArchived: "true" }));
  if (!res.ok) throw new Error("Failed to load categories");
  const json = await res.json();
  return normalizeCategoriesResponse(json);
}

function ColorDot({ color }: { color: string | null }) {
  return (
    <span
      className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10 dark:ring-white/10"
      style={{ backgroundColor: color ?? "#94a3b8" }}
      aria-hidden
    />
  );
}

export function CategoryPicker({
  kind,
  value,
  onChange,
  recentFirst = true,
  className,
  disabled,
}: CategoryPickerProps) {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["categories", kind],
    queryFn: () => fetchCategories(kind),
  });

  // Keep the currently selected category visible even if it got archived
  // out from under the picker.
  const selectable = useMemo(
    () => categories.filter((c) => !c.isArchived || c.id === value),
    [categories, value],
  );

  const [recentIds, setRecentIds] = useState<string[]>([]);
  useEffect(() => {
    // Deliberately deferred to an effect (not a lazy useState initializer):
    // localStorage is client-only, so reading it during the initial render
    // would return [] on the server and diverge on the client, causing a
    // hydration mismatch. Syncing post-mount avoids that at the cost of one
    // extra render, which is the standard fix for this pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRecentIds(recentFirst ? readRecentIds(kind) : []);
  }, [kind, recentFirst]);

  function select(id: string) {
    onChange(id);
    if (recentFirst) setRecentIds(recordRecentId(kind, id));
  }

  const selected = selectable.find((c) => c.id === value) ?? null;
  const parentOf = (c: Category) => selectable.find((p) => p.id === c.parentId) ?? null;

  return (
    <div className={className}>
      <div className="md:hidden" data-testid="category-picker-mobile">
        <MobileChipGrid
          categories={selectable}
          recentIds={recentIds}
          value={value}
          onSelect={select}
          isLoading={isLoading}
          disabled={disabled}
        />
      </div>
      <div className="hidden md:block" data-testid="category-picker-desktop">
        <DesktopDropdown
          categories={selectable}
          value={value}
          selected={selected}
          selectedParent={selected ? parentOf(selected) : null}
          onSelect={select}
          isLoading={isLoading}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function Chip({
  category,
  active,
  onClick,
  disabled,
}: {
  category: Category;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex min-h-10 items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-medium transition-colors active:scale-[0.97]",
        active
          ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300"
          : "border-[var(--cp-line)] bg-[var(--cp-surface)] text-[var(--cp-ink-2)] hover:border-[var(--cp-accent)] hover:bg-[var(--cp-surface-2)]",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <ColorDot color={category.color} />
      {category.name}
    </button>
  );
}

function MobileChipGrid({
  categories,
  recentIds,
  value,
  onSelect,
  isLoading,
  disabled,
}: {
  categories: Category[];
  recentIds: string[];
  value: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
  disabled?: boolean;
}) {
  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  // A root category with no children is not a group — rendering a heading
  // above a lone chip costs a full label row per category and, since every
  // category is currently flat, turned the whole grid into an alternating
  // heading/chip ladder. Ungrouped roots render as one wrapped chip row;
  // only roots that actually have children keep their heading.
  const ungrouped = useMemo(() => tree.filter((p) => p.children.length === 0), [tree]);
  const grouped = useMemo(() => tree.filter((p) => p.children.length > 0), [tree]);
  // orderRecentFirst puts valid (existing, non-archived) recent ids at the
  // front in recency order, then the rest — filtering the reordered list
  // back down to just the recent ids gives that same subset, in that same
  // order, without reimplementing its filtering logic here.
  const recent = useMemo(() => {
    if (recentIds.length === 0) return [];
    const recentIdSet = new Set(recentIds);
    return orderRecentFirst(categories, recentIds).filter((c) => recentIdSet.has(c.id));
  }, [categories, recentIds]);

  if (isLoading) {
    return <div className="text-sm text-[var(--cp-ink-3)]">Loading categories…</div>;
  }
  if (categories.length === 0) {
    return <div className="text-sm text-[var(--cp-ink-3)]">No categories yet.</div>;
  }

  return (
    <div className="space-y-2">
      {recent.length > 0 && (
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
            Recent
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((c) => (
              <Chip
                key={`recent-${c.id}`}
                category={c}
                active={c.id === value}
                onClick={() => onSelect(c.id)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}
      {ungrouped.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ungrouped.map((c) => (
            <Chip
              key={c.id}
              category={c}
              active={c.id === value}
              onClick={() => onSelect(c.id)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
      {grouped.map((parent) => (
        <div key={parent.id}>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
            {parent.name}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Chip
              category={parent}
              active={parent.id === value}
              onClick={() => onSelect(parent.id)}
              disabled={disabled}
            />
            {parent.children.map((child) => (
              <Chip
                key={child.id}
                category={child}
                active={child.id === value}
                onClick={() => onSelect(child.id)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DesktopDropdown({
  categories,
  value,
  selected,
  selectedParent,
  onSelect,
  isLoading,
  disabled,
}: {
  categories: Category[];
  value: string | null;
  selected: Category | null;
  selectedParent: Category | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const shouldReduceMotion = useReducedMotion();

  function closeDropdown() {
    setOpen(false);
    setQuery("");
  }

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeDropdown();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closeDropdown();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    const t = setTimeout(() => searchRef.current?.focus(), 20);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(t);
    };
  }, [open]);

  const filtered = useMemo(() => filterCategoriesByQuery(categories, query), [categories, query]);
  const tree = useMemo(() => buildCategoryTree(filtered), [filtered]);

  const label = selected
    ? selectedParent
      ? `${selectedParent.name} / ${selected.name}`
      : selected.name
    : "Select category…";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled || isLoading}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] px-3 text-sm text-[var(--cp-ink)] transition-colors hover:border-[var(--cp-accent)] disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <span className="flex min-w-0 items-center gap-2 truncate">
          {selected ? <ColorDot color={selected.color} /> : <Tag className="h-3.5 w-3.5 text-[var(--cp-ink-3)]" />}
          <span className="truncate">{isLoading ? "Loading…" : label}</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-[var(--cp-ink-3)] transition-transform", open && "rotate-180")} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.97, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0, scale: 0.97, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 z-30 mt-1 w-full min-w-[260px] overflow-hidden rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] shadow-xl"
          >
            <div className="border-b border-[var(--cp-line)] p-2">
              <div className="flex items-center gap-2 rounded-md border border-[var(--cp-line)] bg-[var(--cp-surface-2)] px-2.5 py-1.5">
                <Search className="h-3.5 w-3.5 shrink-0 text-[var(--cp-ink-3)]" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search categories…"
                  className="w-full bg-transparent text-sm text-[var(--cp-ink)] placeholder:text-[var(--cp-ink-3)] focus:outline-none"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {tree.length === 0 && (
                <p className="px-3 py-4 text-center text-xs text-[var(--cp-ink-3)]">No matching categories</p>
              )}
              {tree.map((parent) => (
                <div key={parent.id}>
                  <Option
                    category={parent}
                    active={parent.id === value}
                    onClick={() => {
                      onSelect(parent.id);
                      closeDropdown();
                    }}
                  />
                  {parent.children.map((child) => (
                    <Option
                      key={child.id}
                      category={child}
                      indent
                      active={child.id === value}
                      onClick={() => {
                        onSelect(child.id);
                        closeDropdown();
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Option({
  category,
  active,
  indent,
  onClick,
}: {
  category: Category;
  active: boolean;
  indent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
        indent && "pl-8",
        active
          ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
          : "text-[var(--cp-ink-2)] hover:bg-[var(--cp-surface-2)]",
      )}
    >
      <ColorDot color={category.color} />
      <span className="flex-1 truncate">{category.name}</span>
      {active && <Check className="h-3.5 w-3.5 shrink-0" />}
    </button>
  );
}
